/**
 * PostHogIdentifyBridge — connects AppContext auth state to PostHog.
 * Calls posthog.identify(user.id, {role, distributor_id}) when the user
 * resolves; posthog.reset() on logout. Renders nothing.
 *
 * Identify timing: AppContext loads `user` first (auth listener), then
 * `role` + `profileDistributorId` in a follow-up profile fetch. PostHog
 * merges properties on repeated identify() calls with the same distinct_id,
 * so this effect re-fires harmlessly as each piece resolves.
 *
 * PII policy (Decision 22): user.id, role, distributor_id only. No email,
 * no full_name.
 */
import { useEffect } from 'react';
import posthog from 'posthog-js';
import { useApp } from '../../hooks/useApp';

export function PostHogIdentifyBridge(): null {
  const { user, role, profileDistributorId } = useApp();

  useEffect(() => {
    if (!import.meta.env.PROD || !import.meta.env.VITE_POSTHOG_KEY) return;
    if (!user) {
      posthog.reset();
      return;
    }
    posthog.identify(user.id, {
      role: role ?? null,
      distributor_id: profileDistributorId ?? null,
    });
  }, [user, role, profileDistributorId]);

  return null;
}
